import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Music, Plus, Eye, MousePointer, ExternalLink, 
  MoreVertical, Trash2, Edit, BarChart3, Copy, Globe, Megaphone
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from "framer-motion";
import Sidebar from "@/components/Sidebar";
import ProFeatureModal from "@/components/ProFeatureModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { getMainDomainUrl } from "@/utils/subdomain";
import { useTheme } from "@/contexts/ThemeContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteMode, setSiteMode] = useState(false);
  const [siteModeLoading, setSiteModeLoading] = useState(false);
  const [proModalOpen, setProModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();

  // Get max pages from user plan config
  // max_pages_limit: -1 means unlimited
  const maxPagesLimit = user?.plan_config?.max_pages_limit;
  const isUnlimited = maxPagesLimit === -1;
  const maxPages = isUnlimited ? Infinity : (maxPagesLimit || 3);
  const canCreateMore = isUnlimited || pages.length < maxPages;

  // Card styles based on theme
  const cardClass = theme === 'dark' 
    ? 'bg-card/50 border-border' 
    : 'bg-white border-gray-200 shadow-sm';

  const handleCreateClick = () => {
    if (canCreateMore) {
      navigate('/multilinks/create');
    } else {
      const submittedEmail = localStorage.getItem('waitlist_email_submitted');
      if (submittedEmail) {
        toast.info(t('proModal', 'alreadySubmitted'));
      } else {
        toast.warning(t('proModal', 'limitReached'));
        setProModalOpen(true);
      }
    }
  };

  useEffect(() => {
    fetchPages();
    // Initialize site mode from user settings
    if (user?.site_navigation_enabled) {
      setSiteMode(true);
    }
  }, [user]);

  const fetchPages = async () => {
    try {
      const response = await api.get("/pages");
      setPages(response.data);
    } catch (error) {
      toast.error(t('errors', 'loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = (page) => {
    setPageToDelete(page);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!pageToDelete) return;
    
    try {
      await api.delete(`/pages/${pageToDelete.id}`);
      toast.success(t('dashboard', 'pageDeleted'));
      setShowDeleteModal(false);
      setPageToDelete(null);
      fetchPages();
    } catch (error) {
      toast.error(t('errors', 'deleteFailed'));
    }
  };

  const duplicatePage = async (page) => {
    try {
      await api.post(`/pages/${page.id}/duplicate`);
      toast.success(t('dashboard', 'pageDuplicated'));
      fetchPages();
    } catch (error) {
      if (error.response?.data?.detail === "PAGE_LIMIT_REACHED") {
        toast.warning(t('proModal', 'limitReached'));
        setProModalOpen(true);
      } else {
        toast.error(t('errors', 'generic'));
      }
    }
  };

  const copyLink = (slug) => {
    // Чистая ссылка для пользователя: https://mus.link/slug
    const url = getMainDomainUrl(`/${slug}`);
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success(t('common', 'copied')))
        .catch(() => toast.error(t('errors', 'copyFailed')));
    } else {
      // Запасной вариант (fallback)
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t('common', 'copied'));
      } catch (err) {
        toast.error(t('errors', 'copyFailed'));
      }
      document.body.removeChild(textArea);
    }
  };

  const getPublicPageUrl = (slug) => getMainDomainUrl(`/${slug}`);

  // Prefer the user-defined unique link (custom_slug) when present.
  const effectiveSlug = (page) =>
    (page?.custom_slug && page.custom_slug.trim()) ? page.custom_slug.trim() : page?.slug;

  const toggleSiteMode = async () => {
    setSiteModeLoading(true);
    try {
      const response = await api.put("/settings/site-navigation", { enabled: !siteMode });
      setSiteMode(response.data.enabled);
      if (refreshUser) await refreshUser();
      toast.success(response.data.enabled ? t('common', 'enabled') : t('common', 'disabled'));
    } catch (error) {
      toast.error(t('errors', 'generic'));
    } finally {
      setSiteModeLoading(false);
    }
  };

  const totalViews = pages.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalClicks = pages.reduce((sum, p) => sum + (p.total_clicks || 0), 0);
  const totalCtr = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : "0.0";

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-10">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold mb-1">{t('auth', 'loginTitle')}, {user?.username}</h1>
            <p className="text-sm sm:text-base text-muted-foreground">{t('dashboard', 'subtitle')}</p>
          </div>
          <Button 
            data-testid="create-page-btn" 
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 rounded-full px-6"
            onClick={handleCreateClick}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard', 'createNew')}
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 sm:p-6 rounded-2xl border ${cardClass}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <span className="text-sm sm:text-base text-muted-foreground">{t('dashboard', 'totalViews')}</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold" data-testid="total-views">{totalViews.toLocaleString()}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`p-4 sm:p-6 rounded-2xl border ${cardClass}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <MousePointer className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              </div>
              <span className="text-sm sm:text-base text-muted-foreground">{t('dashboard', 'totalClicks')}</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold" data-testid="total-clicks">{totalClicks.toLocaleString()}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`p-4 sm:p-6 rounded-2xl border ${cardClass}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Music className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              </div>
              <span className="text-sm sm:text-base text-muted-foreground">{t('dashboard', 'activePages')}</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold" data-testid="total-pages">{pages.length}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`p-4 sm:p-6 rounded-2xl border ${cardClass}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              </div>
              <span className="text-sm sm:text-base text-muted-foreground">CTR</span>
            </div>
            <p className="text-2xl sm:text-3xl font-semibold" data-testid="total-ctr">{totalCtr}%</p>
          </motion.div>
        </div>
        
        {/* Pages List */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('dashboard', 'title')}</h2>
            
            {/* Site Mode Switch */}
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${cardClass}`}>
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{t('dashboard', 'pagination')}</span>
                <Switch
                  checked={siteMode}
                  onCheckedChange={toggleSiteMode}
                  disabled={siteModeLoading || pages.length < 2}
                  data-testid="site-mode-switch"
                />
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : pages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-center py-20 rounded-2xl border border-dashed ${theme === 'dark' ? 'border-white/10' : 'border-gray-300'}`}
            >
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-muted`}>
                <Music className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold mb-2">{t('dashboard', 'noPages')}</h3>
              <p className="text-muted-foreground mb-6">{t('dashboard', 'noPagesDesc')}</p>
              <Button 
                className="bg-primary hover:bg-primary/90 rounded-full"
                onClick={handleCreateClick}
              >
                <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard', 'createNew')}
              </Button>
            </motion.div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {pages.map((page, i) => (
                <motion.div
                  key={page.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border transition-colors w-full overflow-hidden ${cardClass} ${theme === 'dark' ? 'hover:border-white/10' : 'hover:border-gray-300'}`}
                  data-testid={`page-card-${page.id}`}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Cover Image */}
                    <a
                      href={getPublicPageUrl(effectiveSlug(page))}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg sm:rounded-xl overflow-hidden flex-shrink-0 bg-muted transition-opacity hover:opacity-85`}
                      data-testid={`cover-link-${page.id}`}
                      title={t('dashboard', 'viewPage')}
                    >
                      {page.cover_image ? (
                        <img 
                          src={page.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${page.cover_image}` : page.cover_image} 
                          alt={page.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                        </div>
                      )}
                    </a>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="font-semibold text-sm sm:text-base truncate">
                        {page.release_title || page.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate mb-1 sm:mb-2">
                        {page.artist_name || page.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                          {page.views || 0}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <MousePointer className="w-3 h-3 sm:w-4 sm:h-4" />
                          {page.total_clicks || 0}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                          {Number(page.ctr || 0).toFixed(1)}%
                        </span>
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs ${
                          page.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {page.status === 'active' ? t('common', 'active') : t('common', 'inactive')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions - Responsive */}
                    <div className="flex items-center gap-0.5 sm:gap-1 md:gap-2 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 sm:h-9 sm:w-9"
                        onClick={() => copyLink(effectiveSlug(page))}
                        data-testid={`copy-link-${page.id}`}
                        title={t('common', 'copy')}
                      >
                        <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <a 
                        href={getPublicPageUrl(effectiveSlug(page))}
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" data-testid={`view-page-${page.id}`} title={t('dashboard', 'viewPage')}>
                          <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                      </a>
                      <Link to={`/multilinks/${page.id}/edit`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" data-testid={`edit-page-${page.id}`} title={t('dashboard', 'editPage')}>
                          <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                      </Link>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" data-testid={`page-menu-${page.id}`}>
                            <MoreVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border">
                          <DropdownMenuItem asChild>
                            <Link to={`/analytics/${page.id}`} className="flex items-center gap-2">
                              <BarChart3 className="w-4 h-4" />
                              {t('sidebar', 'analytics')}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/multilinks/${effectiveSlug(page)}/promo`} className="flex items-center gap-2">
                              <Megaphone className="w-4 h-4" />
                              {t('sidebar', 'promo') || 'Промо'}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => duplicatePage(page)} className="flex items-center gap-2">
                            <Copy className="w-4 h-4" />
                            {t('dashboard', 'duplicatePage')}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick(page)}
                            className="text-red-400 focus:text-red-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('dashboard', 'deletePage')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PRO Feature Modal */}
      <ProFeatureModal
        open={proModalOpen}
        onOpenChange={setProModalOpen}
        featureName="More Pages"
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && pageToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowDeleteModal(false);
              setPageToDelete(null);
            }}
          />
          
          {/* Modal */}
          <div className="relative bg-card border border-border rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold mb-2">{t('dashboard', 'deletePage')}</h3>
            <p className="text-muted-foreground text-sm mb-2">
              {t('dashboard', 'confirmDelete')}
            </p>
            <p className="text-sm font-medium mb-6 truncate">
              «{pageToDelete.title}»
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowDeleteModal(false);
                  setPageToDelete(null);
                }}
              >
                {t('pageBuilder', 'deleteModalCancel')}
              </Button>
              <Button
                variant="destructive"
                className="flex-1 rounded-xl"
                onClick={confirmDelete}
              >
                {t('pageBuilder', 'deleteModalConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Sidebar>
  );
}
